import { Link, useLocation, useParams } from "react-router-dom";
import LoaderLayout from "./../../../../components/Layout/LoaderLayout";
import {
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { UserContext } from "../../../../Context/UserContextAPI";
import useQueryFetch from "../../../../hooks/useQueryFetch";
import request from "../../../../utils/request";
import MedicalXrayImage from "./Components/MedicalXrayImage";
import MedicalXrayForm from "./Components/MedicalXrayForm";
import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import UserTableInReport from "../../../../components/Users/UserTableInReports";

const MedicalXray = ({ fromEntryPage, xrayEntryRef }) => {
  const { userId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryData = queryParams.get("data") || 2;
  const queryClient = useQueryClient();

  const { settingsData, accessPerm, currentUser } = useContext(UserContext);

  const stageRef = useRef(null);

  // states

  const [radiologyReportData, setRadiologyReportData] = useState([]);

  const [xrayImage, setXrayImage] = useState();

  const [xrayLoading, setXrayLoading] = useState(true);

  const [riskFactor, setRiskFactor] = useState("");

  const [statusValue, setStatusValue] = useState(1);

  const [hasDrawingActivity, setHasDrawingActivity] = useState(false);

  const [isSaving, setIsSaving] = useState(false);

  //   console.log("riskFactor", riskFactor);

  // user detail data fetch
  const { data: userDetailData, isLoading } = useQueryFetch(
    ["user", userId],
    () => request.getOne("user-details/test", userId),
    {
      staleTime: Infinity,
    },
  );
  const userDetail = userDetailData?.userDetail;

  // investigations data fetch
  const { data: investigations, isLoading: isInvestigationsLoading } =
    useQueryFetch(
      ["investigations", queryData, { role_id: userDetail?.user?.role_id }],
      () =>
        request.create(`reports/get-data/${queryData}`, {
          invIds:
            userDetail?.user?.role_id === 5
              ? userDetail?.investigation_id?.split(",")
              : userDetail?.package?.investigation_id?.split(",") || [],
        }),
      {
        staleTime: Infinity,
        enabled: !!userDetail?.user?.role_id,
      },
    );

  // pathology tests data fetch
  const { data: radiologyTests } = useQueryFetch(
    ["radiologyTests", userId],
    () => request.getOne("reports/radiology", userId),
  );

  // fetch comments
  const { data: comments, isSuccess: isCommentsSuccess } = useQueryFetch(
    ["comments", userId],
    () => request.getOne("reports/get-comments", userId),
  );

  const statusData = isCommentsSuccess
    ? comments?.find((item) => item.investigation_group_id === 100)
    : null;

  useEffect(() => {
    if (statusData) {
      setStatusValue(statusData.status);
      setRiskFactor(statusData.comments);
    }
  }, [statusData]);

  //   console.log(radiologyTests);

  useEffect(() => {
    const newRadiologyReportData = [];

    if (radiologyTests?.length > 0) {
      // Handle existing radiology tests
      radiologyTests.forEach((radiologyTest) => {
        const newEntry = {
          user_id: userId,
          investigation_id: radiologyTest.investigation_id || "",
          test_id: radiologyTest.test_id || "",
          user_file: radiologyTest?.user_file,
          result: radiologyTest.result?.replace(/<[^>]*>/g, "") || "",
        };

        // Check for duplicates before adding
        const duplicate = newRadiologyReportData.find(
          (entry) =>
            entry.test_id === newEntry.test_id &&
            entry.investigation_id === newEntry.investigation_id,
        );

        if (!duplicate) {
          newRadiologyReportData.push(newEntry);
        }

        // Handle X-ray image, if test_id is 1 because this test has the xray image
        if (radiologyTest.investigation_id && radiologyTest.test_id === 1) {
          setXrayImage(radiologyTest.user_file);
          setXrayLoading(false);
        }
      });

      setRadiologyReportData(
        newRadiologyReportData.sort((a, b) => a.test_id - b.test_id),
      );
    } else if (investigations?.investigation_tests?.length > 0) {
      // Handle new investigations
      investigations.investigation_tests.forEach((investigation) => {
        newRadiologyReportData.push({
          user_id: userId,
          investigation_id: investigation.investigation_id,
          test_id: investigation.id,
          user_file: "",
          result: "",
        });
      });

      setRadiologyReportData(newRadiologyReportData);
    }
  }, [investigations, userId, radiologyTests]);

  // handler functions

  // set normal  functions
  const handleNormalValue = async () => {
    if (investigations?.investigation_tests) {
      setRadiologyReportData((previousReport) => {
        const updatedReport = previousReport?.map((item, index) => {
          const investigation = investigations?.investigation_tests[index];
          if (investigation) {
            item.result = investigation?.info?.replace(/<[^>]*>/g, "");
            // if (investigation?.id === 35) {
            //   setXrayFindings({
            //     test_id: 35,
            //     info_value: investigation?.info?.replace(/<[^>]*>/g, "").trim(),
            //   });
            // }
          }
          return item;
        });

        return updatedReport?.sort((a, b) => a.test_id - b.test_id);
      });
    }
  };

  // Xrey Upload---------------------------
  const xrayImageUpload = async () => {
    try {
      const imageData = stageRef.current.toDataURL();

      const data = await request.create(`reports/upload-base64-xray-file`, {
        userXrayFile: imageData,
      });
      if (data) {
        setXrayImage(data);
        toast.success("X-ray image uploaded successfully");
        setHasDrawingActivity(false);
      } else {
        toast.error("Failed to upload X-ray image");
      }
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  // Update Report Data-----------------------------
  const saveReportData = async (reportData) => {
    try {
      const res = await request.updateOne(
        "reports/save/radiology/data/all",
        userId,
        {
          radiologyReportData: reportData,
          riskFactor: riskFactor,
          statusValue: statusValue,
        },
        () => {
          queryClient.setQueryData(["radiologyTests", userId], reportData);
          queryClient.setQueryData(
            ["comments", userId],
            [{ ...statusData, status: statusValue }],
          );
          //   queryClient.invalidateQueries(["radiologyTests", userId]);
          //   queryClient.invalidateQueries(["comments", userId]);
        },
      );
      // console.log("res", res);
      return res;
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  // save radiology report
  const saveRadiologyReport = async () => {
    if (![2, 3, 4].includes(statusValue)) {
      return toast.error("Please select a status");
    }

    try {
      setIsSaving(true);
      let newRadiologyReportData = radiologyReportData.map((radio) => {
        if (radio.investigation_id === 31 && radio.test_id === 1) {
          radio.user_file = xrayImage;
        }
        return radio;
      }); // same xray image, if restore image, that restore images will also save

      if (stageRef.current && hasDrawingActivity) {
        const newXrayImg = await xrayImageUpload();
        newRadiologyReportData = radiologyReportData.map((radio) => {
          if (radio.investigation_id === 31 && radio.test_id === 1) {
            radio.user_file = newXrayImg;
          }
          return radio;
        });
      }

      const res = await saveReportData(newRadiologyReportData);
      if (res?.status === "OK") {
        await request.getOne(`reports/user-test-status`, userId);
      }
      // toast.success("Radiology saved successfully.");
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  // button handler in ref for entry page
  useImperativeHandle(xrayEntryRef, () => ({
    handleNormalValue,
    saveRadiologyReport,
  }));

  return (
    <LoaderLayout isPending={isLoading}>
      <div className="">
        <div
          className="card pt-3 px-3 medical-xray"
          //   style={{ paddingBottom: !xrayImage ? "70px" : "10px" }}
        >
          <div
            className="row mb-2 text-white px-2 py-2"
            style={{ backgroundColor: "#1D0CFF" }}
          >
            <h6 className="mb-0">
              Medical X-Ray Report for {userDetail?.user?.f_name}{" "}
              {userDetail?.user?.l_name} [{userDetail?.user?.reg_prefix || ""}
              {userDetail?.user?.registration_no}]
            </h6>
          </div>
        </div>

        <div className="row justify-content-center h-100">
          {userDetail && !fromEntryPage && (
            <UserTableInReport
              user={{
                ...userDetail?.user,
                userDetail: userDetail,
              }}
            />
          )}
        </div>

        {accessPerm(33, 28) && (
          <LoaderLayout isPending={isInvestigationsLoading}>
            <div className="d-flex justify-content-between mt-1 gap-3 ">
              {/* xray form  */}

              <div style={{ width: "600px" }}>
                <MedicalXrayForm
                  investigations={investigations}
                  radiologyReportData={radiologyReportData}
                  setRadiologyReportData={setRadiologyReportData}
                  setStatusValue={setStatusValue}
                  statusValue={statusValue}
                />
                <div className="py-0 rounded-bottom">
                  <div className="d-flex justify-content-between">
                    <div className="d-flex gap-2 align-items-center">
                      {!fromEntryPage &&
                        userDetailData?.nextPage &&
                        settingsData?.next_prev_button === 1 && (
                          <Link
                            to={`/dashboard/medical-reports/radiology/create/${userDetailData?.nextPage}?data=2`}
                            className="btn btn-sm fs-6 px-3 py-2 text-white"
                            style={{
                              backgroundColor:
                                userDetail?.userDetail?.user?.status === 2
                                  ? "#28a745" // bg-success
                                  : userDetail?.userDetail?.user?.status === 3
                                    ? "#dc3545" // bg-danger
                                    : userDetail?.userDetail?.user?.status === 4
                                      ? "#0000FF" // bg-primary
                                      : "#6c757d", // default color
                            }}
                          >
                            Previous
                          </Link>
                        )}
                      {!fromEntryPage &&
                        userDetailData?.previousPage &&
                        settingsData?.next_prev_button === 1 && (
                          <Link
                            to={`/dashboard/medical-reports/radiology/create/${userDetailData?.previousPage}?data=2`}
                            className="btn btn-sm fs-6 px-3 py-2 text-white"
                            style={{
                              backgroundColor:
                                userDetail?.userDetail?.user?.status === 2
                                  ? "#28a745" // bg-success
                                  : userDetail?.userDetail?.user?.status === 3
                                    ? "#dc3545" // bg-danger
                                    : userDetail?.userDetail?.user?.status === 4
                                      ? "#0000FF" // bg-primary
                                      : "#6c757d", // default color
                            }}
                          >
                            Next
                          </Link>
                        )}
                    </div>

                    <div className="d-flex gap-2 justify-content-end align-items-center">
                      {accessPerm(33, 22) && (
                        <button
                          className="btn btn-primary px-2 btn-sm fs-6 w-100 p-2"
                          onClick={handleNormalValue}
                          style={{ whiteSpace: "nowrap" }}
                        >
                          Set Normal
                        </button>
                      )}
                      {!fromEntryPage && (
                        <button
                          className="btn btn-success px-2 btn-md fs-6 w-100 p-2"
                          onClick={saveRadiologyReport}
                          style={{ whiteSpace: "nowrap" }}
                          disabled={isSaving}
                        >
                          {isSaving ? (
                            <div className="d-flex align-items-center gap-2">
                              <span
                                className="spinner-border"
                                style={{ width: "1rem", height: "1rem" }}
                              ></span>
                              <span>Saving...</span>
                            </div>
                          ) : (
                            "Save All"
                          )}
                        </button>
                      )}

                      {!fromEntryPage && (
                        <button
                          className="btn btn-dark text-white btn-md fs-6 w-100 p-2"
                          style={{ whiteSpace: "nowrap" }}
                          onClick={() => {
                            window.close();
                          }}
                        >
                          Close
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* xray image  */}
              <MedicalXrayImage
                setRiskFactor={setRiskFactor}
                xrayImage={xrayImage}
                xrayLoading={xrayLoading}
                userDetail={userDetail}
                setXrayImage={setXrayImage}
                setXrayLoading={setXrayLoading}
                stageRef={stageRef}
                setHasDrawingActivity={setHasDrawingActivity}
              />
            </div>
          </LoaderLayout>
        )}
      </div>
    </LoaderLayout>
  );
};

export default MedicalXray;
